import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useDelayUnmount } from '../../Hooks'
import { Option } from './props'

const getMenuWidth = (ref: React.RefObject<HTMLDivElement>) => {
    const { width } = ref.current?.getBoundingClientRect() ?? {}

    return width ?? 0
}

export const useSelect = <O extends Option>({
    isClearable,
    isSearchable,
    onChange,
    options,
    value,
}: {
    isClearable: boolean
    isSearchable: boolean
    onChange: (option: O | undefined) => void
    options: O[]
    value?: O
}) => {
    const selectRef = useRef<HTMLDivElement>(null)

    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isSearching, setIsSearching] = useState(false)
    const [searchWord, setSearchWord] = useState<string>('')
    const [selectedOption, setSelectedOption] = useState<O | undefined>(value)
    const [menuWidth, setMenuWidth] = useState(getMenuWidth(selectRef))

    const selectText = useMemo(
        () =>
            isSearchable && isSearching
                ? searchWord
                : selectedOption
                ? selectedOption.label
                : '',
        [isSearchable, isSearching, searchWord, selectedOption]
    )

    const showClearIcon = useMemo(
        () => isClearable && selectedOption !== undefined,
        [isClearable, selectedOption]
    )

    const showClearIconDelay = useDelayUnmount(showClearIcon, 200)

    const filteredOptions = useMemo(
        () =>
            isSearching
                ? options.filter((option) =>
                      option.label
                          .toLowerCase()
                          .includes(searchWord.toLowerCase())
                  )
                : options,
        [isSearching, options, searchWord]
    )

    useEffect(() => setSelectedOption(value), [value])

    const onChangeOption = useCallback(
        (option: O | undefined) => {
            onChange(option)
            setSelectedOption(option)
            setIsMenuOpen(false)
        },
        [onChange]
    )

    const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsSearching(true)
        setSearchWord(e.target.value)
        onChange(undefined)
        setSelectedOption(undefined)
    }

    const onMouseDownOption = useCallback(
        (option: O | undefined) => {
            setIsSearching(false)
            setSearchWord('')
            onChangeOption(option)
        },
        [onChangeOption]
    )

    const onClickInput = useCallback(() => {
        setIsMenuOpen(!isMenuOpen)
        setMenuWidth(getMenuWidth(selectRef))
    }, [isMenuOpen])

    return {
        filteredOptions,
        isMenuOpen,
        menuWidth,
        onChangeInput,
        onChangeOption,
        onMouseDownOption,
        onClickInput,
        searchWord,
        selectRef,
        selectText,
        selectedOption,
        showClearIcon,
        showClearIconDelay,
    }
}
